import React from "react";
import styled, { keyframes } from "styled-components";

export interface AnimatedProps {
    animation?: "fadeIn" | "slideIn" | "scaleIn" | TransitionStyles;
    duration?: number;
    delay?: number;
    children?: React.ReactNode;
    animatedProperties?: string; // CSS properties to animate
    in?: boolean; // Transition state (for TransitionStyles)
    hideOnExit?: boolean; // Hide element on exit
}

export interface CustomAnimationConfig {
    AnimatedComponent: React.ComponentType<any>;
    props?: Record<string, any>;
}

export interface TransitionStyles {
    entering?: React.CSSProperties;
    entered?: React.CSSProperties;
    exiting?: React.CSSProperties;
    exited?: React.CSSProperties;
}

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideIn = keyframes`
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

const scaleIn = keyframes`
    from { transform: scale(0.9); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
`;

const AnimatedRoot = styled.div<AnimatedProps>`
    animation: ${({ animation }) => {
            if (animation === "slideIn") return slideIn;
            if (animation === "scaleIn") return scaleIn;
            return fadeIn;
        }}
        ${({ duration }) => duration || 0.3}s ease-in-out
        ${({ delay }) => delay || 0}s;
`;

const AnimatedComponent: React.FC<AnimatedProps> = ({ children, animation = "fadeIn", duration, delay }) => {
    return (
        <AnimatedRoot animation={animation} duration={duration} delay={delay}>
            {children}
        </AnimatedRoot>
    );
};

export const CustomAnimatedComponent: React.FC<AnimatedProps & { component?: React.ElementType }> = ({
    component: Component = "div",
    children,
    ...props
}) => {
    return (
        <AnimatedComponent {...props}>
            {React.createElement(Component, {}, children)}
        </AnimatedComponent>
    );
};

// FadingSlide sub-component
const FadingSlide: React.FC<{ direction?: "up" | "down" | "left" | "right"; in?: boolean; delay?: number; children?: React.ReactNode }> = ({
    direction = "up",
    in: inProp,
    delay,
    children,
}) => {
    const getTransform = () => {
        if (!inProp) {
            if (direction === "up") return "translateY(20px)";
            if (direction === "down") return "translateY(-20px)";
            if (direction === "left") return "translateX(20px)";
            if (direction === "right") return "translateX(-20px)";
        }
        return "translate(0)";
    };

    return (
        <div
            style={{
                opacity: inProp ? 1 : 0,
                transform: getTransform(),
                transition: `all ${delay || 300}ms ease-in-out`,
            }}
        >
            {children}
        </div>
    );
};

// Create compound component
export const Animated = Object.assign(AnimatedComponent, {
    FadingSlide,
});

export default Animated;


